import { useState, useMemo, useEffect } from "react";
import {
  isAllChild,
  PARENT_LABELS,
  OPS_NUMERIC,
  OPS_TEXT,
} from "../../../../util/helper";
import { isRemovedJobParent } from "../../../../util/removedJobGuards";

const normalizeChildValue = (parent, child) => {
  if (parent === "molecule_id" && child === "molecule_id::id") {
    return "molecule_id::molecule_id";
  }
  if (parent === "reward" && child === "reward::value") {
    return "reward::reward";
  }
  return child;
};

const normalizeRules = (rules) =>
  Array.isArray(rules)
    ? rules
        .filter((rule) => rule?.parent && !isRemovedJobParent(rule.parent))
        .map((rule) => ({
          ...rule,
          child: normalizeChildValue(rule.parent, rule.child),
        }))
    : [];

const getChildType = (childValue) => {
  if (!childValue || isAllChild(childValue)) return "auto";
  const [group, tag] = childValue.split("::");
  if (group === "molecule_id") return "text";
  if (group === "reward") return "number";
  if (group === "glide" && tag === "file") return "number";
  if (group === "phase" && tag === "file") return "text";
  if (group === "molprops") return "number";

  if (group === "adme") {
    if (tag && tag.toLowerCase().includes("solubility - htsa ph6 classifier")) {
      return "boolean";
    }
    return "number";
  }

  return "auto";
};

const evalOperator = (rowVal, op, targetVal, declaredType = "auto") => {
  const sRow = String(rowVal ?? "").toLowerCase();
  const sTgt = String(targetVal ?? "").toLowerCase();

  if (declaredType === "boolean" && (op === "eq" || op === "neq")) {
    const norm = (value) => {
      if (typeof value === "boolean") return value;
      const text = String(value).toLowerCase();
      if (text === "true") return true;
      if (text === "false") return false;
      return null;
    };

    const left = norm(rowVal);
    const right = norm(targetVal);
    if (left === null || right === null) return false;
    return op === "eq" ? left === right : left !== right;
  }

  if (op === "contains") {
    return sRow.includes(sTgt);
  }

  const asNumRow = Number(rowVal);
  const asNumTarget = Number(targetVal);
  const numeric =
    declaredType === "number" ||
    (declaredType === "auto" && Number.isFinite(asNumRow) && Number.isFinite(asNumTarget));

  if (numeric && ["gt", "gte", "lt", "lte", "eq", "neq"].includes(op)) {
    switch (op) {
      case "gt": return asNumRow > asNumTarget;
      case "gte": return asNumRow >= asNumTarget;
      case "lt": return asNumRow < asNumTarget;
      case "lte": return asNumRow <= asNumTarget;
      case "eq": return asNumRow === asNumTarget;
      case "neq": return asNumRow !== asNumTarget;
      default: break;
    }
  }

  if (op === "eq") return sRow === sTgt;
  if (op === "neq") return sRow !== sTgt;

  return true;
};

const getFieldValueForRule = (row, childValue) => {
  if (!childValue || isAllChild(childValue)) return undefined;
  const parts = childValue.split("::");
  const group = parts[0];
  const tag = parts[1];
  const arg1 = parts[2];
  const arg2 = parts[3];

  if (group === "molecule_id") {
    const rawId = row.molecule_id ?? row.moleculeId ?? row.smile_id ?? row.smileId ?? row.smile ?? "";
    return String(rawId);
  }
  if (group === "reward") return row.reward;
  if (group === "molprops") return row[tag];
  if (group === "adme") return row[tag]?.value;

  if (group === "glide" && tag === "file") {
    const item = Array.isArray(row.glide)
      ? row.glide.find((g) => String(g.file) === String(arg1) && String(g.ph_threshold) === String(arg2))
      : null;
    return item?.score;
  }

  if (group === "phase" && tag === "file") {
    const item = Array.isArray(row.phase)
      ? row.phase.find((p) => String(p.file) === String(arg1) && String(p.ph_threshold) === String(arg2))
      : null;
    return item?.matched_ligand_sites;
  }

  return undefined;
};

const evaluateRule = (row, rule, childOptionsByParent) => {
  if (!rule?.parent || !rule?.child || !rule?.op) return true;

  if (["is_passed", "is_failed", "is_true", "is_false"].includes(rule.op)) {
    const [group, tag] = (rule.child || "").split("::");
    const value = getFieldValueForRule(row, rule.child);

    if (group === "adme" && getChildType(rule.child) === "boolean") {
      const isTrue = value === true;
      return rule.op === "is_true" ? isTrue : !isTrue;
    }

    if (group === "glide" && tag === "file") {
      if (!row.glide || !Array.isArray(row.glide)) return false;
      if (value === undefined) return false;
      const passed = Number.isFinite(Number(value));
      const failed = value === "Failed";
      if (rule.op === "is_passed") return passed;
      if (rule.op === "is_failed") return failed;
    }

    if (group === "phase" && tag === "file") {
      if (!row.phase || !Array.isArray(row.phase)) return false;
      if (value === undefined) return false;
      const passed = !!(value && String(value).trim());
      return rule.op === "is_passed" ? passed : !passed;
    }
  }

  if (rule.parent === "glide" && rule.child?.startsWith("glide::file")) {
    const value = getFieldValueForRule(row, rule.child);
    if (value === "Failed") return false;
  }

  if (isAllChild(rule.child)) {
    const children = childOptionsByParent[rule.parent] || [];
    if (!children.length) return false;
    return children.some((option) => {
      const value = getFieldValueForRule(row, option.value);
      return evalOperator(value, rule.op, rule.value, getChildType(option.value));
    });
  }

  const value = getFieldValueForRule(row, rule.child);
  return evalOperator(value, rule.op, rule.value, getChildType(rule.child));
};

const applyRulesToRows = (rows, rules, childOptionsByParent) => {
  if (!rules?.length) return rows;
  return rows.filter((row) => {
    const firstResult = evaluateRule(row, rules[0], childOptionsByParent);
    if (rules.length === 1) return firstResult;
    return rules.slice(1).reduce((accumulator, currentRule) => {
      const currentResult = evaluateRule(row, currentRule, childOptionsByParent);
      if ((currentRule.glue || "and") === "or") return accumulator || currentResult;
      return accumulator && currentResult;
    }, firstResult);
  });
};

const useStickyList = (list = []) => {
  const safeList = useMemo(() => (Array.isArray(list) ? list : []), [list]);
  const [cached, setCached] = useState(safeList);

  useEffect(() => {
    if (safeList.length || !cached.length) {
      setCached(safeList);
    }
  }, [safeList, cached.length]);

  return cached;
};

const readPersistedRules = (storageKey) => {
  if (!storageKey || typeof window === "undefined") return [];

  try {
    const saved = sessionStorage.getItem(storageKey);
    if (!saved) return [];
    return normalizeRules(JSON.parse(saved));
  } catch {
    return [];
  }
};

export const useAdvancedFilter = (baseData, dynamicColumnGroups, storageKey) => {
  const [rulesDraft, setRulesDraft] = useState(() => readPersistedRules(storageKey));
  const [rulesAppliedState, setRulesAppliedState] = useState(() => readPersistedRules(storageKey));
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const { dynamicMolProps, dynamicAdmeColumns, dynamicGlideColumns, dynamicPhaseColumns } = dynamicColumnGroups;
  const rulesApplied = rulesAppliedState;
  const setRulesApplied = (rules) => setRulesAppliedState(normalizeRules(rules));

  useEffect(() => {
    const persistedRules = readPersistedRules(storageKey);
    setRulesDraft(persistedRules);
    setRulesAppliedState(persistedRules);
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || typeof window === "undefined") return;

    if (!rulesApplied.length) {
      sessionStorage.removeItem(storageKey);
      return;
    }

    sessionStorage.setItem(storageKey, JSON.stringify(rulesApplied));
  }, [storageKey, rulesApplied]);

  const stickyMolProps = useStickyList(dynamicMolProps);
  const stickyAdmeCols = useStickyList(dynamicAdmeColumns);
  const stickyGlideCols = useStickyList(dynamicGlideColumns);
  const stickyPhaseCols = useStickyList(dynamicPhaseColumns);

  const childOptionsByParent = useMemo(() => {
    const molpropsArr = stickyMolProps.map((column) => ({
      label: column.title,
      value: `molprops::${column.sourceKey}`,
      type: "number",
    }));

    const admeArr = stickyAdmeCols.map((column) => ({
      label: `${column.parent}(${column.child})`,
      value: `adme::${column.fullKey}`,
      type: "auto",
    }));

    const glideArr = stickyGlideCols.map((column) => ({
      label: `${column.file} (pH=${column.ph_threshold})`,
      value: `glide::file::${column.file}::${column.ph_threshold}`,
    }));

    const phaseArr = stickyPhaseCols.map((column) => ({
      label: `${column.file} (pH=${column.ph_threshold})`,
      value: `phase::file::${column.file}::${column.ph_threshold}`,
    }));

    const moleculeIdArr = [{ label: "Molecule ID", value: "molecule_id::molecule_id", type: "text" }];
    const rewardArr = [{ label: "Reward", value: "reward::reward", type: "number" }];

    return {
      all: [...molpropsArr, ...admeArr, ...glideArr, ...phaseArr, ...moleculeIdArr, ...rewardArr],
      molprops: molpropsArr,
      adme: admeArr,
      glide: glideArr,
      phase: phaseArr,
      molecule_id: moleculeIdArr,
      reward: rewardArr,
    };
  }, [stickyMolProps, stickyAdmeCols, stickyGlideCols, stickyPhaseCols]);

  const filteredData = useMemo(
    () => applyRulesToRows(baseData, rulesApplied, childOptionsByParent),
    [baseData, rulesApplied, childOptionsByParent]
  );

  return {
    filteredData,
    filterDrawerOpen,
    setFilterDrawerOpen,
    rulesDraft,
    setRulesDraft,
    rulesApplied,
    setRulesApplied,
    childOptionsByParent,
    getChildType,
    OPS_NUMERIC,
    OPS_TEXT,
    PARENT_LABELS,
  };
};
